import React, { useState, useCallback, useEffect } from 'react';
import Table from '@cloudscape-design/components/table';
import Header from '@cloudscape-design/components/header';
import ContentLayout from '@cloudscape-design/components/content-layout';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Alert from '@cloudscape-design/components/alert';
import Spinner from '@cloudscape-design/components/spinner';
import { scenariosService } from '../services/scenarios';
import type { Scenario, ScenarioStatus } from '../types';

/**
 * Status options for the scenario status dropdown.
 */
const STATUS_OPTIONS: SelectProps.Option[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

/**
 * Industry options for the scenario industry dropdown.
 */
const INDUSTRY_OPTIONS: SelectProps.Option[] = [
  { label: 'Financial Services', value: 'Financial Services' },
  { label: 'Healthcare and Life Sciences', value: 'Healthcare and Life Sciences' },
  { label: 'E-commerce', value: 'E-commerce' },
  { label: 'Non-Profit', value: 'Non-Profit' },
  { label: 'Technology', value: 'Technology' },
  { label: 'Education', value: 'Education' },
  { label: 'Public Sector', value: 'Public Sector' },
  { label: 'Manufacturing', value: 'Manufacturing' },
];

/**
 * Get the status option object for a given status value.
 *
 * @param status - The scenario status value
 * @returns The corresponding SelectProps.Option
 */
function getStatusOption(status: ScenarioStatus): SelectProps.Option {
  return STATUS_OPTIONS.find((option) => option.value === status) ?? STATUS_OPTIONS[0];
}

/**
 * Get the industry option object for a given industry value.
 *
 * @param industry - The scenario industry value
 * @returns The corresponding SelectProps.Option
 */
function getIndustryOption(industry: string): SelectProps.Option {
  return INDUSTRY_OPTIONS.find((option) => option.value === industry) ?? { label: industry, value: industry };
}

/**
 * Interface for the edit form state.
 */
interface EditFormState {
  name: string;
  industry: SelectProps.Option | null;
  status: SelectProps.Option | null;
}

/**
 * Admin Scenario Management Page - Admin panel for scenario management
 *
 * Displays a table of all training scenarios with management capabilities:
 * - Scenarios table with name, industry, and status columns
 * - Edit action button for each scenario
 * - Edit form modal for modifying scenario details
 * - Information note about Bedrock Prompt Management integration
 * - Loading spinner during data fetch
 * - Error alert on API failure
 *
 * Features:
 * - Uses Cloudscape Design System components
 * - Status indicators for active/inactive state
 * - Modal form for editing scenario details
 * - Informational alert about future versioning integration
 * - Fetches real data from the scenarios API service
 *
 * @validates Requirements 5.3 - Fetch data from real API using scenarios service
 * @validates Requirements 5.4 - Display loading indicators while API requests are in progress
 * @validates Requirements 5.5 - Display error states when API requests fail
 * @validates Requirements 4.6 - Display error alert when API request returns non-2xx
 * @validates Requirements 4.7 - Do not silently display empty or mock data on failure
 */
export function AdminScenarioManagementPage(): React.ReactElement {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [scenarioToEdit, setScenarioToEdit] = useState<Scenario | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>({
    name: '',
    industry: null,
    status: null,
  });

  /**
   * Load scenarios from the API.
   * Sets loading state, fetches data, and handles errors.
   */
  const loadScenarios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await scenariosService.getScenarios();
      setScenarios(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  /**
   * Open the edit modal for a scenario.
   * Populates the form with the scenario's current values.
   *
   * @param scenario - The scenario to edit
   */
  const handleEditClick = useCallback((scenario: Scenario): void => {
    setScenarioToEdit(scenario);
    setEditForm({
      name: scenario.name,
      industry: getIndustryOption(scenario.industry),
      status: getStatusOption(scenario.status),
    });
    setEditModalVisible(true);
  }, []);

  /**
   * Handle form field changes.
   *
   * @param field - The field to update
   * @param value - The new value
   */
  const handleFormChange = useCallback(
    <K extends keyof EditFormState>(field: K, value: EditFormState[K]): void => {
      setEditForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  /**
   * Save the edited scenario.
   * Calls the scenarios service to persist changes, then reloads the list.
   */
  const handleSaveScenario = useCallback(async (): Promise<void> => {
    if (scenarioToEdit && editForm.industry && editForm.status) {
      try {
        await scenariosService.updateScenario(scenarioToEdit.id, {
          name: editForm.name,
          industry: editForm.industry.value as string,
          status: editForm.status.value as ScenarioStatus,
        });
        await loadScenarios();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save scenario');
      }
    }
    setEditModalVisible(false);
    setScenarioToEdit(null);
  }, [scenarioToEdit, editForm, loadScenarios]);

  /**
   * Cancel editing and close the modal.
   */
  const handleCancelEdit = useCallback((): void => {
    setEditModalVisible(false);
    setScenarioToEdit(null);
  }, []);

  return (
    <ContentLayout
      data-testid="admin-scenario-management-page"
      header={
        <Header
          variant="h1"
          description="Manage training scenarios and their content"
        >
          Scenario Management
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Error alert when API request fails */}
        {error && (
          <Alert
            type="error"
            dismissible
            onDismiss={() => setError(null)}
            header="Error loading scenarios"
            data-testid="error-alert"
          >
            {error}
          </Alert>
        )}

        {/* Bedrock Prompt Management Integration Note */}
        <Alert
          type="info"
          header="Bedrock Prompt Management Integration"
          data-testid="bedrock-integration-note"
        >
          Scenario versioning will be integrated with Amazon Bedrock Prompt Management in a future
          release. This will enable advanced prompt versioning, A/B testing, and prompt optimization
          capabilities for training scenarios.
        </Alert>

        {/* Loading spinner during initial fetch */}
        {loading && (
          <Box textAlign="center" padding={{ vertical: 'l' }}>
            <Spinner size="large" />
            <Box variant="p" margin={{ top: 's' }}>Loading scenarios...</Box>
          </Box>
        )}

        {!loading && (
          <Table
            data-testid="scenarios-table"
            columnDefinitions={[
              {
                id: 'name',
                header: 'Name',
                cell: (item: Scenario) => item.name,
                sortingField: 'name',
                width: 300,
              },
              {
                id: 'industry',
                header: 'Industry',
                cell: (item: Scenario) => item.industry,
                sortingField: 'industry',
                width: 150,
              },
              {
                id: 'status',
                header: 'Status',
                cell: (item: Scenario) => (
                  <StatusIndicator
                    type={item.status === 'active' ? 'success' : 'stopped'}
                    data-testid={`status-indicator-${item.id}`}
                  >
                    {item.status === 'active' ? 'Active' : 'Inactive'}
                  </StatusIndicator>
                ),
                sortingField: 'status',
                width: 120,
              },
              {
                id: 'actions',
                header: 'Actions',
                cell: (item: Scenario) => (
                  <Button
                    variant="normal"
                    onClick={() => handleEditClick(item)}
                    data-testid={`edit-button-${item.id}`}
                  >
                    Edit
                  </Button>
                ),
                width: 100,
              },
            ]}
            items={scenarios}
            loading={loading}
            loadingText="Loading scenarios..."
            trackBy="id"
            variant="container"
            header={
              <Header
                variant="h2"
                description="View and manage training scenarios"
                counter={`(${scenarios.length})`}
              >
                Scenarios
              </Header>
            }
            empty={
              <Box
                margin={{ vertical: 'xs' }}
                textAlign="center"
                color="inherit"
              >
                <SpaceBetween size="m">
                  <b>No scenarios found</b>
                  <Box variant="p" color="inherit">
                    There are no training scenarios configured.
                  </Box>
                </SpaceBetween>
              </Box>
            }
          />
        )}
      </SpaceBetween>

      {/* Edit Scenario Modal */}
      <Modal
        visible={editModalVisible}
        onDismiss={handleCancelEdit}
        header="Edit Scenario"
        closeAriaLabel="Close modal"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={handleCancelEdit}
                data-testid="cancel-edit-button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveScenario}
                data-testid="save-scenario-button"
              >
                Save Changes
              </Button>
            </SpaceBetween>
          </Box>
        }
        data-testid="edit-scenario-modal"
      >
        <Form>
          <SpaceBetween size="l">
            <FormField label="Scenario Name" data-testid="name-form-field">
              <Input
                value={editForm.name}
                onChange={({ detail }) => handleFormChange('name', detail.value)}
                placeholder="Enter scenario name"
                data-testid="name-input"
              />
            </FormField>

            <FormField label="Industry" data-testid="industry-form-field">
              <Select
                selectedOption={editForm.industry}
                onChange={({ detail }) => handleFormChange('industry', detail.selectedOption)}
                options={INDUSTRY_OPTIONS}
                placeholder="Select industry"
                selectedAriaLabel="Selected"
                data-testid="industry-select"
              />
            </FormField>

            <FormField label="Status" data-testid="status-form-field">
              <Select
                selectedOption={editForm.status}
                onChange={({ detail }) => handleFormChange('status', detail.selectedOption)}
                options={STATUS_OPTIONS}
                placeholder="Select status"
                selectedAriaLabel="Selected"
                data-testid="status-select"
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </Modal>
    </ContentLayout>
  );
}

export default AdminScenarioManagementPage;
